import type { GameModuleManifest } from "@melodimals/game-core";

import { isDuoEnabled } from "@/lib/p2p/signaling-availability";

/**
 * Main menu module registry. Components are loaded lazily in
 * their routes (Vite code-splitting); only the manifest + destination live here,
 * and the home generates its cards from this. Adding a module = manifest + route.
 */

interface RegisteredModule {
  manifest: GameModuleManifest;
  /** Destination of the home card. */
  to: string;
  params?: Record<string, string>;
  /**
   * Off feature that governs the module. If its switch is not on,
   * the module is NOT shown: it is better for it not to exist than to exist and fail.
   */
  feature?: "duo";
}

export const GAME_MODULES: readonly RegisteredModule[] = [
  {
    manifest: {
      id: "piano",
      kind: "instrument",
      icon: "🎹",
      accentClass: "bg-joy-sky",
      pedagogicalGoal: "free-play-fine-motor",
    },
    to: "/instrument/$instrumentId",
    params: { instrumentId: "piano" },
  },
  {
    manifest: {
      id: "xylophone",
      kind: "instrument",
      icon: "🌈",
      accentClass: "bg-joy-sunshine",
      pedagogicalGoal: "free-play-pitch-discrimination",
    },
    to: "/instrument/$instrumentId",
    params: { instrumentId: "xylophone" },
  },
  {
    manifest: {
      id: "drums",
      kind: "instrument",
      icon: "🥁",
      accentClass: "bg-joy-coral",
      pedagogicalGoal: "free-play-rhythm",
    },
    to: "/instrument/$instrumentId",
    params: { instrumentId: "drums" },
  },
  {
    manifest: {
      id: "songs",
      kind: "songs",
      icon: "🎵",
      accentClass: "bg-joy-mint",
      pedagogicalGoal: "guided-melody-memory",
    },
    to: "/songs",
  },
  {
    manifest: {
      id: "simon",
      kind: "game",
      icon: "🐵",
      accentClass: "bg-joy-grass",
      pedagogicalGoal: "auditory-memory-sequencing",
    },
    to: "/games/simon",
  },
  {
    manifest: {
      id: "who-sounds",
      kind: "game",
      icon: "👂",
      accentClass: "bg-joy-bubblegum",
      pedagogicalGoal: "timbre-discrimination-vocabulary",
    },
    to: "/games/who-sounds",
  },
  {
    manifest: {
      id: "duo",
      kind: "game",
      icon: "🐵🐸",
      accentClass: "bg-joy-grape",
      pedagogicalGoal: "turn-taking-social-play",
    },
    to: "/games/duo",
    /**
     * RETIRED from the product (2026-08-03). The switch is off by default on all
     * platforms, so this module never comes out of `availableGameModules()`: there is no
     * card on the home, no sticker in the collection, and no registered `/games/duo` route.
     * The entry is kept —like the implementation— so that turning it back on
     * is configuration, not archaeology. See `docs/P2P_SIGNALING_ENABLEMENT.md`.
     */
    feature: "duo",
  },
  {
    manifest: {
      id: "count-and-play",
      kind: "game",
      icon: "⭐",
      accentClass: "bg-joy-tangerine",
      pedagogicalGoal: "counting-one-to-one-correspondence",
    },
    to: "/games/count-and-play",
  },
];

/**
 * Modules offerable in THIS runtime. It is evaluated on every call (it is not a module
 * constant) because the config is injected at startup time, not at build time.
 *
 * Everything that enumerates modules —the home, the sticker collection and the route tree— must
 * use this: if the home hides a module but the collection still counts its sticker, the
 * child is left with a collection impossible to complete 100%.
 */
export function availableGameModules(): readonly RegisteredModule[] {
  return GAME_MODULES.filter((m) => m.feature !== "duo" || isDuoEnabled());
}
